import type { StyleRule } from '@archipro-design/aria';
import { inputClassName, labelClassName } from '@archipro-design/aria';
import { searchBarClassName } from '@archipro-design/aria';
import { pxToRem, pxArrayToRem, buttonClassName } from '@archipro-design/aria';
import type { ColorScheme } from '@archipro-design/aria';
import { svgIconClassName } from '@fluentui/react-icons-northstar';

const FOCUS_COLOR = '#ECECEC';

export const SearchBarMobile: StyleRule<{ isFocus: boolean }> = ({
    theme,
    isFocus,
}) => {
    return {
        display: 'flex',
        justifyContent: 'space-between',
        gap: pxToRem(5),
        alignItems: 'center',
        padding: pxArrayToRem([0, 10, 13]),
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        [`& .${searchBarClassName}`]: {
            height: pxToRem(44),
            flexGrow: 1,
            backgroundColor: isFocus ? FOCUS_COLOR : 'transparent',
            border: `1px solid ${isFocus ? FOCUS_COLOR : '#D3D3D3'}`,

            [`& input`]: {
                color: isFocus
                    ? theme.siteVariables.colors.charcoal[250]
                    : '#757575',
                '&::placeholder': {
                    color: '#757575',
                },
            },
        },
        [`& .${svgIconClassName}`]: {
            opacity: isFocus ? 1 : 0.4,
        },
    };
};

export const MobileButtonCancel: StyleRule = ({ theme }) => ({
    [`& .${labelClassName}`]: {
        ...theme.siteVariables.textStyles.Heading.Roman55,
    },
});

export const SearchBarDesktop: StyleRule = ({ isEmptyKeyword, theme }) => {
    const colorScheme = theme.siteVariables.colorScheme.default as ColorScheme;
    const TRANSITION = '300ms ease';
    return {
        marginTop: pxToRem(-1),
        overflow: 'hidden',
        ':hover': {
            [`& .${searchBarClassName}`]: {
                [`& input`]: {
                    '::placeholder': {
                        color:
                            colorScheme['searchPlaceholderHover'] ??
                            colorScheme['foreground5'],
                    },
                },
            },
        },

        [`& .${searchBarClassName}`]: {
            margin: 0,
            padding: 0,
            height: pxToRem(43),
            width: isEmptyKeyword ? pxToRem(100) : pxToRem(340),
            borderRadius: 0,
            background: 'transparent',

            [`& .${buttonClassName}:last-child`]: {
                margin: pxArrayToRem([0, 0, 0, 12]),
                padding: 0,
                borderRadius: '50%',
                width: pxToRem(24),
                height: pxToRem(24),
                '&:hover': {
                    background:
                        theme.siteVariables.colors.transparency.black['6%'],
                },
                [`& > span`]: {
                    padding: 0,
                },
            },

            [`& .${inputClassName}`]: {
                padding: 0,
                width: pxToRem(262),
            },

            [`& input`]: {
                paddingTop: pxToRem(2),
                fontSize: pxToRem(18),
                '::-webkit-search-cancel-button': {
                    display: 'none',
                },

                letterSpacing: theme.siteVariables.letterSpacingSmall,
                '&::placeholder': {
                    letterSpacing: 0,
                    color:
                        colorScheme['searchPlaceholderNotHover'] ??
                        theme.siteVariables.colorScheme.default?.foreground,
                },
            },
        },

        '& .animated-underline': {
            bottom: 0,
            left: 0,
            transform: 'translate3d(-200%, 0, 0)',
            transition: `transform ${TRANSITION}`,
            width: pxToRem(300),
            height: '2px',
            backgroundColor:
                colorScheme['searchAnimatedUnderline'] ??
                theme.siteVariables.colorScheme.default?.foreground,
        },

        ':focus': {
            '& .animated-underline': {
                transform: 'translate3d(0%, 0, 0)',
            },
        },
        ':focus-within': {
            '& .animated-underline': {
                transform: 'translate3d(0%, 0, 0)',
            },
        },
    };
};
